
import React from 'react';
import { Lead, ViewType } from '@/types/lead';
import CardView from '@/components/CardView';
import ListView from '@/components/ListView';
import TableView from '@/components/TableView';
import EmptyState from '@/components/EmptyState';
import { Button } from '@/components/ui/button';

interface LeadsContentProps {
  isLoading: boolean;
  error: string | null;
  filteredLeads: Lead[];
  currentView: ViewType;
  searchQuery: string;
  clearSearch: () => void;
  isSearching: boolean;
  handleSearch: () => void;
  handleToggleFavorite: (id: string) => void;
  handleDelete: (id: string) => void;
  handleAddNote: (leadId: string, content: string) => void;
  handleAddDocument: (leadId: string, name: string, url: string, type: string) => void;
}

const LeadsContent: React.FC<LeadsContentProps> = ({
  isLoading,
  error,
  filteredLeads,
  currentView,
  searchQuery,
  clearSearch,
  isSearching,
  handleSearch,
  handleToggleFavorite,
  handleDelete,
  handleAddNote,
  handleAddDocument,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center space-y-5">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent bg-gradient-to-r from-primary via-primary/50 to-primary bg-[length:200%_200%] [mask-image:linear-gradient(#000_0_0),linear-gradient(#000_0_0)] [mask-composite:exclude] mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-primary/30 rounded-full animate-pulse"></div>
            </div>
          </div>
          <div>
            <p className="text-base font-semibold tracking-tight">Loading your leads</p>
            <p className="text-sm text-muted-foreground mt-1">Please wait a moment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center p-8 rounded-xl border border-destructive/20 bg-destructive/5 max-w-md">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="text-lg font-semibold tracking-tight text-destructive mb-2">Error Loading Leads</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button 
            variant="outline" 
            className="hover:bg-destructive/5 hover:border-destructive/30"
            onClick={handleSearch}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  if (filteredLeads.length === 0) {
    return (
      <EmptyState 
        searchQuery={searchQuery} 
        onClearSearch={searchQuery ? clearSearch : undefined}
        isSearching={isSearching || searchQuery !== ''}
      />
    );
  }

  switch (currentView) {
    case 'card':
      return (
        <CardView 
          leads={filteredLeads} 
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
          onAddNote={handleAddNote}
          onAddDocument={handleAddDocument}
        />
      );
    case 'list':
      return (
        <ListView 
          leads={filteredLeads} 
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
        />
      );
    case 'table':
      return (
        <TableView 
          leads={filteredLeads} 
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
        />
      );
    default:
      return null;
  }
};

export default LeadsContent;
